import type { ApiResponse } from '../types/api';

export interface ApiClientConfig {
  baseUrl: string;
  apiKey?: string;
  token?: string;
  /** Called per-request to fetch the current Bearer token. Takes precedence over `token`. */
  getToken?: () => string | null | undefined;
  onError?: (error: ApiClientError) => void;
}

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export class ApiClient {
  private config: ApiClientConfig;

  constructor(config: ApiClientConfig) {
    this.config = config;
  }

  private get headers(): Record<string, string> {
    const h: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const dynamicToken = this.config.getToken?.();
    if (dynamicToken) {
      h['Authorization'] = `Bearer ${dynamicToken}`;
    } else if (this.config.token) {
      h['Authorization'] = `Bearer ${this.config.token}`;
    } else if (this.config.apiKey) {
      h['X-API-Key'] = this.config.apiKey;
    }
    return h;
  }

  private async request<T>(method: string, path: string, body?: unknown, query?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<T>> {
    let url = `${this.config.baseUrl}${path}`;
    if (query) {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== '') params.append(k, String(v));
      }
      const qs = params.toString();
      if (qs) url += `?${qs}`;
    }
    const res = await fetch(url, {
      method,
      headers: this.headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const json: ApiResponse<T> = await res.json();

    if (!res.ok || !json.success) {
      const err = new ApiClientError(
        res.status,
        json.error?.code ?? 'UNKNOWN',
        json.error?.message ?? 'An error occurred',
        json.error?.details,
      );
      this.config.onError?.(err);
      throw err;
    }

    return json;
  }

  get<T>(path: string, query?: Record<string, string | number | boolean | undefined>) { return this.request<T>('GET', path, undefined, query); }
  post<T>(path: string, body?: unknown) { return this.request<T>('POST', path, body); }
  put<T>(path: string, body?: unknown) { return this.request<T>('PUT', path, body); }
  patch<T>(path: string, body?: unknown) { return this.request<T>('PATCH', path, body); }
  delete<T>(path: string) { return this.request<T>('DELETE', path); }

  async upload<T>(path: string, file: File, fields?: Record<string, string>): Promise<ApiResponse<T>> {
    const url = `${this.config.baseUrl}${path}`;
    const form = new FormData();
    form.append('file', file);
    if (fields) {
      for (const [k, v] of Object.entries(fields)) {
        form.append(k, v);
      }
    }
    const headers: Record<string, string> = {};
    if (this.config.apiKey) headers['X-API-Key'] = this.config.apiKey;
    if (this.config.token) headers['Authorization'] = `Bearer ${this.config.token}`;

    const res = await fetch(url, { method: 'POST', headers, body: form });
    const json: ApiResponse<T> = await res.json();
    if (!res.ok || !json.success) {
      const err = new ApiClientError(
        res.status,
        json.error?.code ?? 'UNKNOWN',
        json.error?.message ?? 'Upload failed',
        json.error?.details,
      );
      this.config.onError?.(err);
      throw err;
    }
    return json;
  }

  updateConfig(config: Partial<ApiClientConfig>) {
    Object.assign(this.config, config);
  }
}
